import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type BoatClass, ApiError } from '../../lib/api.ts';
import { FlagGlyph, FLAG_OPTIONS } from '../../components/SignalFlags.tsx';

export function AdminClasses() {
  const qc = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<BoatClass | null>(null);

  const list = useQuery({
    queryKey: ['classes', includeInactive],
    queryFn: () => api.listClasses(includeInactive),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Båtklasser</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Ny klasse
        </button>
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-400 mb-3">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Vis arkiverte
      </label>

      {(showNew || editing) && (
        <ClassForm
          initial={editing ?? undefined}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['classes'] });
          }}
        />
      )}

      {list.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {(list.data ?? []).map((c) => (
            <li
              key={c.id}
              className={`p-3 bg-slate-900/30 ${!c.active ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                <FlagGlyph code={c.flag} className="w-9 h-6 shrink-0 rounded-sm" />
                <div className="font-mono text-xs w-24 text-slate-400 truncate">{c.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.name}</div>
                  {c.description && (
                    <div className="text-xs text-slate-400 truncate">{c.description}</div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setEditing(c);
                    setShowNew(false);
                  }}
                  className="text-xs text-sky-400"
                >
                  Rediger
                </button>
              </div>
            </li>
          ))}
          {list.data?.length === 0 && (
            <li className="p-6 text-center text-slate-500 text-sm">
              Ingen klasser. Opprett den første.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ClassForm({ initial, onClose }: { initial?: BoatClass; onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [sortOrder, setSortOrder] = useState<number>(initial?.sortOrder ?? 100);
  const [flag, setFlag] = useState(initial?.flag ?? 'G');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (initial) {
        return api.updateClass(initial.id, {
          code: code.toUpperCase(),
          name,
          description: description || null,
          sortOrder,
          flag,
        });
      }
      return api.createClass({
        code: code.toUpperCase(),
        name,
        description: description || null,
        sortOrder,
        flag,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes'] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const archive = useMutation({
    mutationFn: async () => initial && api.archiveClass(initial.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes'] });
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">{initial ? 'Rediger klasse' : 'Ny klasse'}</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Kode (A-Z, 0-9, _)</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            pattern="[A-Z0-9_]+"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
            placeholder="J70"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Sortering</label>
          <input
            type="number"
            min={0}
            max={9999}
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Visningsnavn</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          placeholder="J/70"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Beskrivelse (valgfritt)</label>
        <textarea
          value={description ?? ''}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">
          Klasseflagg (vises i startprosedyren)
        </label>
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
          {FLAG_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              type="button"
              onClick={() => setFlag(opt.code)}
              title={`${opt.code} – ${opt.name}`}
              className={`flex flex-col items-center gap-1 p-1 rounded border transition ${
                flag === opt.code
                  ? 'border-sky-400 bg-sky-900/30'
                  : 'border-slate-700 hover:border-slate-500'
              }`}
            >
              <FlagGlyph code={opt.code} className="w-full h-7" />
              <span className="text-[10px] font-mono text-slate-400">{opt.code}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
      {initial && initial.active && (
        <button
          type="button"
          onClick={() => {
            if (confirm(`Arkivere klassen "${initial.name}"?`)) archive.mutate();
          }}
          className="w-full text-xs text-red-400 py-2"
        >
          Arkiver klasse
        </button>
      )}
    </form>
  );
}
